import styled from "@emotion/styled";
import { Box, Button, Checkbox, Divider, FormControlLabel, FormGroup, Link, Typography } from "@mui/material";
import { FC, useRef, useState } from "react";
import CustomInput from "../../../../../../../../components/_common/CustomInput";
import { useToast } from "../../../../../../../../hooks/useToast";
import FormHelper from "../../../../../../../../helpers/form.helper";
import CustomButton from "../../../../../../../../components/_common/CustomButton";
import RegulationsAlert from "../../../../../../../../components/_common/RegulationsAlert";
import LoadingAnimation from "../../../../../../../../components/_common/Loading";
import { IModule } from "../../../../../../../../interfaces/IFurniture";
import moduleValuationController, { ICreateModuleValuationDto } from "../../../../../../../../Api/moduleValuationController";
import { useModal } from "../../../../../../../../hooks/useModal";


const MyInput = styled(CustomInput)`
    width: 100%;
    margin-top: 20px;
`

const MyDivider = styled(Divider)`
    margin-top: 20px;
`


interface CustomerFormProps {
    onBackToProject: () => void;
    modules?: IModule[];
    standAloneModules?: IModule[];
    furnitureId: string;
    furnitureName: string;
    furnitureImage: string;
    furnitureLink: string;
}

const CustomerForm: FC<CustomerFormProps> = ({
    onBackToProject,
    modules,
    standAloneModules,
    furnitureId,
    furnitureName,
    furnitureImage,
    furnitureLink
}) => {
    const [email, setEmail] = useState<string>("")
    const [phone, setPhone] = useState<string>("")
    const [message, setMessage] = useState<string>("")
    const [configuratorAgreement, setConfiguratorAgreement] = useState<boolean>(false)
    const [regulationsAgreement, setRegulationsAgreement] = useState<boolean>(false)
    const [showRegulations, setShowRegulations] = useState<boolean>(false)
    const [showErrors, setShowErrors] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const toast = useRef(useToast()).current;
    const modal = useModal()


    const handleClickButton = async () => {
        setLoading(true);

        setShowErrors(true)
        if (
            !FormHelper.isEmail(email) ||
            !FormHelper.isPhoneNumber(phone) ||
            !regulationsAgreement ||
            !configuratorAgreement
        ) {
            setLoading(false)
            return toast.error('Wypełnij poprawnie wszystkie pola')
        }

        const data: ICreateModuleValuationDto = {
            modules: modules || [],
            standAloneModules: standAloneModules || [],
            message,
            furnitureId,
            furnitureName,
            furnitureImage,
            furnitureLink,
            email,
            phone,
        }

        const res = await moduleValuationController.createModuleValuation(data)
        if (!res.ok) {
            setLoading(false)
            return toast.error(res.message)
        }

        toast.success(res.message)
        setLoading(false)
        modal.close()
    }

    if (loading) return <LoadingAnimation />

    return (
        <>
            <Box
                onClick={onBackToProject}
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <Button
                    variant='contained'
                    color='secondary'
                    sx={{ width: '600px', maxWidth: '100%', mt: 4 }}
                >
                    Wróc do projektowania
                </Button>
            </Box>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
            }}>
                <Box
                    component='form'
                    autoComplete="on"
                    sx={{
                        maxWidth: '600px',
                    }}
                >
                    <MyDivider sx={{ marginBottom: '20px' }} />
                    <Typography variant="h6">
                        Dane kontaktowe
                    </Typography>
                    <MyInput
                        id="email"
                        mode="email"
                        error={showErrors && (FormHelper.isEmail(email) ? false : true)}
                        value={email}
                        setValue={setEmail}
                        variant='filled'
                    />
                    <MyInput
                        id="phone"
                        mode="tel"
                        error={showErrors && (FormHelper.isPhoneNumber(phone) ? false : true)}
                        value={phone}
                        setValue={setPhone}
                        variant='filled'
                    />
                    <MyDivider variant="middle" />
                    <MyInput
                        mode="multiline"
                        value={message}
                        setValue={setMessage}
                        variant="filled"
                        maxRows={10}
                        label="Podaj tkaninę jeżeli masz wybraną, lub dodatkowe informacje"
                    />
                    <MyDivider variant="middle" />
                    <FormGroup sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={regulationsAgreement}
                                    onChange={() => {
                                        regulationsAgreement ? setRegulationsAgreement(false) : setShowRegulations(true)
                                    }}
                                    inputProps={{ 'aria-label': 'controlled' }}
                                />
                            }
                            label={
                                <Typography>
                                    {'* Akceptuję '}
                                    <Link
                                        color="secondary"
                                        onClick={() => { setShowRegulations(true) }}
                                    >
                                        regulamin
                                    </Link>
                                    {' sprzedaży produktów oferowanych przez spółkę UFO Sp. z o.o.'}
                                </Typography>
                            }
                        />
                    </FormGroup>
                    <MyDivider sx={{ mb: 2 }} />
                    <FormControlLabel
                        required
                        control={<Checkbox
                            onClick={() => setConfiguratorAgreement(!configuratorAgreement)}
                        />} label="* Konfigurator ma charakter poglądowy i nie może być podstawą do składania zamówień. Przed złożeniem zamówienia obowiązkowo należy zapoznać się z wymiarami w katalogu. Zamówienia oparte wyłącznie na konfiguratorze nie będą uwzględniane, a ewentualne błędy wymiarowe nie podlegają reklamacji." />
                    <CustomButton
                        disabled={!regulationsAgreement || !configuratorAgreement}
                        text="Zamów wycenę"
                        onClick={handleClickButton}
                        sx={{ width: '100%', marginTop: '10px', marginBottom: '20vh' }}
                    />
                    <RegulationsAlert
                        open={showRegulations}
                        handleSetOpen={setShowRegulations}
                        handleAgree={() => setRegulationsAgreement(true)}
                        handleDisagree={() => setRegulationsAgreement(false)}
                    />
                </Box>
            </Box>
        </>
    )
}

export default CustomerForm;